import { UseFormReturn } from 'react-hook-form';
import { IDocumentTable } from '@models/Forms/IForms';
import './Table.scss';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { useEffect, useMemo, useState } from 'react';
import EditTable from './controls/EditTable';
import ViewTable from './controls/ViewTable';

export interface ITableProps<TFieldValues extends object = object> {
    table: IDocumentTable;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
}

const Table = <TFieldValues extends object = object>({
    table,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    ...props
}: ITableProps<TFieldValues>) => {
    const visibilityRules = table?.visibilityRules;
    const visibilityMng = useMemo(() => {
        const manager = new FormulaManager(visibilityRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [visibilityRules, fields, formMethods]);

    const activated = React.useRef(false);
    const [visibility, setVisibility] = useState<boolean>(false);

    const dataField = fields[table.key];
    if (dataField) {
        includedFields.push(dataField.index);
    }

    const InitFormulas = async () => {
        const vis = await visibilityMng.EvalFormulaValues(isEdit, isNew);
        if (activated.current) {
            setVisibility(vis);
        }
    };

    useEffect(() => {
        activated.current = true;
        InitFormulas();
        return () => {
            activated.current = false;
        };
    }, []);

    return table && table.key && table.tableColumn && visibility && dataField ? (
        isEdit || isNew ? (
            <EditTable
                table={table}
                formMethods={formMethods}
                setError={setError}
                isEdit={isEdit}
                isNew={isNew}
                turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                fields={fields}
                docId={docId}
            />
        ) : (
            <ViewTable table={table} formMethods={formMethods} fields={fields} docId={docId} />
        )
    ) : (
        <></>
    );
};

export default Table;
